import React, { useState } from "react"
import "../../css/Controls.css"
import { ReactComponent as Nubbin } from "../../icon/popover-nubbin.svg"
import { ColorResult, SketchPicker } from 'react-color'
import Config from "../../library/Config"

interface SettingsProps {
  config: Config
  show: boolean
  numberOfAds: number
  dashboardLink: string | null
  didChangeConfig: Function
}

interface Event {
  target: {
    value: string
  }
}

export default function SettingsPopover({ config, show, numberOfAds, dashboardLink, didChangeConfig }: SettingsProps) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [initialBackgroundColor, setInitialBackgroundColor] = useState(config.backgroundColor)

  const PHOTO_DELAY_MIN = 0.5
  const PHOTO_DELAY_MAX = 10

  const CYCLE_END_DELAY_MIN = 2
  const CYCLE_END_DELAY_MAX = 60
  const CYCLE_END_DELAY_SUGGESTED = 5

  const ROWS_MIN = 1
  const ROWS_MAX = 8

  const COLS_MIN = 1
  const COLS_MAX = 12

  const AD_DURATION_MIN = 1
  const AD_DURATION_MAX = 60

  const CYCLES_BETWEEN_ADS_MIN = 1
  const CYCLES_BETWEEN_ADS_MAX = 20

  const getConfigCopy = () => {
    return { ...config }
  }

  const constrainNumber = (number: number, min: number, max: number) => {
    if (number > max) {
      number = max
    }
    if (number < min) {
      number = min
    }
    return number
  }

  const changeRecentLimit = (e: Event) => {
    let newConfig = getConfigCopy()
    newConfig.recentLimit = parseInt(e.target.value)
    didChangeConfig(newConfig)
  }

  const toggleFeatureNewImages = () => {
    let newConfig = getConfigCopy()
    newConfig.featureNewImages = !config.featureNewImages
    didChangeConfig(newConfig)
  }

  const changeTransitionEffect = (e: Event) => {
    let newConfig = getConfigCopy()
    newConfig.transition = e.target.value
    didChangeConfig(newConfig)
  }

  const didChangeRefreshMode = (e: Event) => {
    let newConfig = getConfigCopy()
    let mode = e.target.value
    newConfig.refreshMode = mode

    switch (mode) {
      case "screen":
        newConfig.photoDelay = 0.1
        newConfig.cycleEndDelay = Math.max(newConfig.cycleEndDelay, CYCLE_END_DELAY_SUGGESTED)
        break;
      case "single":
        newConfig.photoDelay = 1
        newConfig.cycleEndDelay = 1
        break;
      default:
        break;
    }

    didChangeConfig(newConfig)
  }

  const changePhotoDelay = (e: Event) => {
    let newConfig = getConfigCopy()
    let value = parseFloat(e.target.value)
    newConfig.photoDelay = constrainNumber(value, PHOTO_DELAY_MIN, PHOTO_DELAY_MAX)
    didChangeConfig(newConfig)
  }

  const changeCycleEndDelay = (e: Event) => {
    let newConfig = getConfigCopy()
    let value = parseFloat(e.target.value)
    newConfig.cycleEndDelay = constrainNumber(value, CYCLE_END_DELAY_MIN, CYCLE_END_DELAY_MAX)
    didChangeConfig(newConfig)
  }

  const toggleAutoLayout = () => {
    let newConfig = getConfigCopy()
    newConfig.autoLayout = !config.autoLayout
    didChangeConfig(newConfig)
  }

  const changeRows = (e: Event) => {
    let newConfig = getConfigCopy()
    let value = parseInt(e.target.value)
    newConfig.rows = constrainNumber(value, ROWS_MIN, ROWS_MAX)
    didChangeConfig(newConfig)
  }

  const changeColumns = (e: Event) => {
    let newConfig = getConfigCopy()
    let value = parseInt(e.target.value)
    newConfig.columns = constrainNumber(value, COLS_MIN, COLS_MAX)
    didChangeConfig(newConfig)
  }

  const changeAdDuration = (e: Event) => {
    let newConfig = getConfigCopy()
    let value = parseFloat(e.target.value)
    newConfig.adDuration = constrainNumber(value, AD_DURATION_MIN, AD_DURATION_MAX)
    didChangeConfig(newConfig)
  }

  const changeCyclesBetweenAds = (e: Event) => {
    let newConfig = getConfigCopy()
    let value = parseInt(e.target.value)
    newConfig.cyclesBetweenAds = constrainNumber(value, CYCLES_BETWEEN_ADS_MIN, CYCLES_BETWEEN_ADS_MAX)
    didChangeConfig(newConfig)
  }

  const toggleLimitAds = () => {
    let newConfig = getConfigCopy()
    newConfig.showOneAdPerCycle = !config.showOneAdPerCycle
    didChangeConfig(newConfig)
  }

  const toggleColorPicker = (e: any) => {
    e.preventDefault()
    setInitialBackgroundColor(config.backgroundColor)
    setShowColorPicker(!showColorPicker)
  }

  const changeBackgroundColor = (color: ColorResult) => {
    changeBackgroundColorHex(color.hex)
  }

  const changeBackgroundColorHex = (hex: string) => {
    let newConfig = getConfigCopy()
    newConfig.backgroundColor = hex
    didChangeConfig(newConfig)
  }

  return (
    <div id="popoverContainer" className={show ? "show" : "hide"}>
      <div id="nubbin">
        <Nubbin />
      </div>
      <div id="popoverInside">
        <section id="editBackgroundColor" style={{ transform: showColorPicker ? "translateX(0%)" : "translateX(100%)" }}>
          <fieldset>

          <div className="row">
            <legend>Edit Background Color</legend> <button className="link" style={{ textAlign: "right" }} onClick={() => changeBackgroundColorHex(initialBackgroundColor)}>Revert</button>
            </div>
            
            <div className="row">
              <div className="threeCol">
                <SketchPicker
                  color={config.backgroundColor}
                  disableAlpha
                  onChange={changeBackgroundColor}
                  width={"100%"}
                  presetColors={[config.galleryBackgroundColor, config.galleryTextColor]}
                  styles={{ picker: {} }}
                />
              </div>
            </div>
            <div className="row" style={{ overflow: "visible" }}>
              <button onClick={toggleColorPicker}>Done</button>
            </div>
          </fieldset>
        </section>
        <section style={{ transform: !showColorPicker ? "translateX(0%)" : "translateX(-100%)" }}>
          <fieldset>
            <legend>Photos</legend>
            <div className="row">
              <div className="threeCol">
                <label>Show</label>
                <select id="recentLimit" defaultValue={config.recentLimit} onChange={changeRecentLimit}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
                <label>most recent photos</label>
              </div>
            </div>

            <div className="row">
              <div className="threeCol">
                <input type="checkbox" checked={config.featureNewImages} onChange={toggleFeatureNewImages} />
                <label onClick={toggleFeatureNewImages}>Feature new photos full-screen</label>
              </div>
            </div>
          </fieldset>

          <fieldset className="separate">
            <div className="row">
              <legend>Transitions</legend>
            </div>

            <div className="row">
              <label>Refresh mode</label>
              <select className="oneCol" defaultValue={config.refreshMode} onChange={didChangeRefreshMode}>
                <option value="screen">Cascade</option>
                <option value="single">Individual</option>
              </select>
            </div>

            <div className="row">
              <label>Visual effect</label>
              <select className="oneCol" defaultValue={config.transition} onChange={changeTransitionEffect}>
                <option value="scale">Scale</option>
                <option value="rotate">Rotate</option>
                <option value="slide">Slide</option>
                <option value="blur">Blur</option>
                <option value="fade">Fade</option>
              </select>
            </div>

            {config.refreshMode === "single" &&
              <div className="row">
                <label>Delay between photos</label>
                <div className="oneCol">
                  <input id="photoDelay" type="number" min={PHOTO_DELAY_MIN} max={PHOTO_DELAY_MAX} step={0.5} value={config.photoDelay} onChange={changePhotoDelay} />
                  <span className="units">sec</span>
                </div>
              </div>
            }

            {config.refreshMode === "screen" &&
              <div className="row">
                <label>Delay at end of cycle</label>
                <div className="oneCol">
                  <input id="cycleEndDelay" type="number" min={CYCLE_END_DELAY_MIN} max={CYCLE_END_DELAY_MAX} step={0.5} value={config.cycleEndDelay} onChange={changeCycleEndDelay} />
                  <span className="units">sec</span>
                </div>
              </div>
            }
          </fieldset>

          <fieldset className="separate">

            <div className="row">
              <legend>Layout</legend>
              <div className="oneCol alignRight">
                <small>{config.rows * config.columns} images</small>
              </div>
            </div>

            <fieldset>
              <div className="row">
                <div className="threeCol">
                  <input type="checkbox" checked={config.autoLayout} onChange={toggleAutoLayout} />
                  <label onClick={toggleAutoLayout}>Auto</label>
                </div>
              </div>
            </fieldset>

            <fieldset disabled={config.autoLayout}>
              <div className="row">
                <label>Rows</label>
                <div className="oneCol alignRight">
                  <input type="number" name="rows" min={ROWS_MIN} max={ROWS_MAX} value={config.rows} onChange={changeRows} />
                </div>
              </div>

              <div className="row">
                <label>Columns</label>
                <div className="oneCol alignRight">
                  <input type="number" name="columns" min={COLS_MIN} max={COLS_MAX} value={config.columns} onChange={changeColumns} />
                </div>
              </div>
            </fieldset>

            <div className="row" style={{ overflow: "visible" }}>
              <label>Background color</label>
              <div className="oneCol alignRight">
                <button id="colorButton" onClick={toggleColorPicker} style={{ background: config.backgroundColor }}></button>
              </div>
            </div>
          </fieldset>

          <fieldset className="separate" disabled={numberOfAds === 0}>
            <div className="row">
              <legend>Ads</legend>
              <div className="oneCol alignRight">
                <small>{numberOfAds} ad{numberOfAds !== 1 && "s"}</small>
              </div>
            </div>

            <div className="row">
              <label>Cycles between ads</label>
              <div className="oneCol">
                <input type="number" min={CYCLES_BETWEEN_ADS_MIN} max={CYCLES_BETWEEN_ADS_MAX} step={1} defaultValue={config.cyclesBetweenAds} onChange={changeCyclesBetweenAds} />
                <span className="units">cycles</span>
              </div>
            </div>

            <div className="row">
              <label>Default ad duration</label>
              <div className="oneCol">
                <input type="number" min={AD_DURATION_MIN} max={AD_DURATION_MAX} step={1} defaultValue={config.adDuration} onChange={changeAdDuration} />
                <span className="units">sec</span>
              </div>
            </div>

            <fieldset disabled={numberOfAds <= 1}>
              <div className="row">
                <div className="threeCol">
                  <input type="checkbox" checked={config.showOneAdPerCycle} onChange={toggleLimitAds} />
                  <label onClick={numberOfAds >= 2 ? toggleLimitAds : () => void 0}>Only show one ad per cycle</label>
                </div>
              </div>
            </fieldset>

            {dashboardLink &&
              <div className="row">
                <div className="threeCol">
                  <small>You can add and remove ads from the <a href={dashboardLink} target="_blank" rel="noreferrer">dashboard</a>.</small>
                </div>
              </div>
            }
          </fieldset>
        </section>
      </div>
    </div>
  )
}